// Выделения в тексте дизайн-блока — геометрия из макета Figma («Group 32», текст
// 70 px на холсте 1654) в долях кегля, как в мобилке
// (`shared/rich-text/styles.ts`). Маркер и волна рисуются ФОНОМ строчного
// элемента, а не паддингом/`text-decoration`: текст стоит ровно там же, где
// стоял бы без выделения, а у `wavy` из CSS не задать ни размах, ни период.
import type { HighlightTokens } from './designElementStyles'

// Верх «рамки» строчного элемента (content area) над базовой линией: hhea
// ascender Wix Madefor Display — 1008 при unitsPerEm 1000. От этой рамки
// браузер отсчитывает фон, поэтому все вертикали ниже пересчитаны от неё.
// ⚠️ Число — ИМЕННО от Madefor; на другой гарнитуре пересчитать.
const ASCENT = 1.008

/** Маркер: выступает за рамку слова на 0.114 em по бокам, от 0.886 em над
 *  базовой линией (в макете на 3 px ниже — подняли по просьбе дизайнера) до
 *  0.229 em под ней, скругление 0.286 em. */
const MARKER = { outX: 8 / 70, above: 62 / 70, below: 16 / 70, radius: 20 / 70 }
/** Подчёркивание: толщина 0.1 em, верх линии на 0.114 em ниже базовой. */
const UNDERLINE = { offset: 8 / 70, thickness: 7 / 70 }
/** Волна: синусоида толщиной 0.1 em, размах 0.114 em, период 0.763 em. Стоит на
 *  уровне прямого подчёркивания — верх гребней там же, где верх его линии (в
 *  макете ось на 0.36 em ниже базовой — ниже выносных, пользователь попросил
 *  поднять): хвосты «р», «д», «у» ложатся поверх волны, как поверх
 *  подчёркивания. Фон строчного элемента рисуется под его буквами. */
const WAVE_STROKE = 7 / 70
const WAVE = {
  offset: UNDERLINE.offset + WAVE_STROKE / 2,
  amplitude: 8 / 70,
  period: 53.4 / 70,
  stroke: WAVE_STROKE,
}

const em = (n: number): string => `${Math.round(n * 1000) / 1000}em`

/** Заливка маркера — скруглённый прямоугольник: `border-radius` скругляет весь
 *  фон строчного элемента, а он выше нужного (вся рамка шрифта 1.26 em), поэтому
 *  маркер — одна SVG-картинка в полосе нужной высоты, как в плеере
 *  (highlightStyles.ts): прямоугольник во всю ширину со скруглением в долях
 *  высоты (ry в %, rx = ry), углы круглые при любой ширине слова. Раньше он был
 *  собран из полос и кругов: каждый слой округлялся до пикселя по-своему, и на
 *  стыках оставались щели и торчащие «палочки». Цвет — hex (`blockTheme`). */
const markerBackground = (color: string): string => {
  const top = ASCENT - MARKER.above
  const height = MARKER.above + MARKER.below
  const ry = Math.round((MARKER.radius / height) * 10000) / 100
  const svg = `<svg xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" ry="${ry}%" fill="${color}"/></svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 0 ${em(top)} / 100% ${em(height)} no-repeat`
}

/** Тайл волны — один период в сотых долях em: полупериоды кубическими кривыми,
 *  как в SVG из Figma, начиная с верхней точки. */
const waveTile = (color: string): string => {
  const p = WAVE.period * 100
  const s = WAVE.stroke * 100
  const lo = s / 2 + WAVE.amplitude * 100
  const hi = s / 2
  const q = (n: number) => Math.round(n * 100) / 100
  const d = `M0 ${hi}C${q(p / 4)} ${hi} ${q(p / 4)} ${q(lo)} ${q(p / 2)} ${q(lo)}C${q((p * 3) / 4)} ${q(lo)} ${q((p * 3) / 4)} ${hi} ${q(p)} ${hi}`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${q(p)} ${q(lo + s / 2)}" preserveAspectRatio="none"><path d="${d}" fill="none" stroke="${color}" stroke-width="${s}"/></svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

const waveBackground = (color: string): string =>
  `${waveTile(color)} 0 ${em(ASCENT + WAVE.offset - WAVE.stroke / 2)} / ${em(WAVE.period)} ${em(WAVE.amplitude + WAVE.stroke)} repeat-x`

// Стили выделений цепляются за ТЕГИ (mark/u/strong/span[data-wavy]), а не за классы —
// бек вырезает `class`/`style`, поэтому классы tiptap не переживут перезагрузку
// страницы. Общий CSS-блок для RichTextView (read-only) и RichTextEditor (edit) —
// оба должны рендерить выделения идентично.
//
// Скоуп в каждом селекторе утроен (`.scope.scope.scope`), а не опечатка: конкурент —
// `.tiptap.ProseMirror strong { color: inherit }` (src/index.css), 0,2,1 (2 класса +
// tag). Прежний `.scope strong` весил 0,1,1 и проигрывал — акцент затирался на
// inherit, автор видел чёрный текст вместо оранжевого (WYSIWYG-расхождение с
// read-only режимом). Теги в селекторе не спасают: класс-ярус сравнивается раньше
// тегового, `.scope p strong` (0,1,2) всё ещё проигрывает 0,2,1 — нужно поднять
// именно число классов. Утроение даёт 0,3,1 > 0,2,1 БЕЗУСЛОВНО, вне зависимости от
// порядка подключения `<style>` (порядок — не наша зона контроля). Не упрощать
// обратно до одного класса.
export const highlightScopeStyles = (scope: string, highlight: HighlightTokens): string => {
  const s = `.${scope}.${scope}.${scope}`
  // Паддинг выносит заливку за рамку слова, отрицательный маргин возвращает текст на место.
  const out = em(MARKER.outX)
  return `
    ${s} mark { color: ${highlight.markerText}; background: ${markerBackground(highlight.marker)}; padding: 0 ${out}; margin: 0 -${out}; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
    ${s} u { text-decoration-line: underline; text-decoration-style: solid; text-decoration-color: ${highlight.accent}; text-decoration-thickness: ${em(UNDERLINE.thickness)}; text-underline-offset: ${em(UNDERLINE.offset)}; text-decoration-skip-ink: none; }
    ${s} [data-wavy] { text-decoration: none; background: ${waveBackground(highlight.accent)}; padding-bottom: 0.25em; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
    ${s} strong, ${s} b { color: ${highlight.accent}; }
  `
}
